export type AssistTileMask = 0 | 1 | 2

export interface TouchTileDecision {
  selectedIdx: number
  confirmDiscard: boolean
}

export interface DiscardSourceDecision {
  isTedashi: boolean
  displayIdx: number
}

export function isDrawnTilePosition(handCount: number, index: number, afterCall: boolean): boolean {
  return !afterCall && handCount % 3 === 2 && index === handCount - 1
}

export function sortHandForDisplay<T extends { code: number }>(tiles: readonly T[], keepLastAsDrawn: boolean): T[] {
  const sortedLength = keepLastAsDrawn ? Math.max(0, tiles.length - 1) : tiles.length
  const sorted = tiles.slice(0, sortedLength).sort((left, right) => left.code - right.code)
  return keepLastAsDrawn && tiles.length > 0 ? [...sorted, tiles[tiles.length - 1]] : sorted
}

export const DISCARD_SOURCE_MARKER_DEPTH = -1

export function offsetDiscardSourceMarker(
  position: { x: number; y: number },
  openOffset: { x: number; y: number },
  scale: number,
): { x: number; y: number } {
  return {
    x: position.x + openOffset.x * scale,
    y: position.y + openOffset.y * scale,
  }
}

export function waitGuideWorldY(
  handTop: number,
  visibleTop: number,
  visibleBottom: number,
  guideHeight = 85,
  actionButtonHeight = 40,
  gap = 8,
): number {
  const paddedMinY = visibleTop + gap
  const paddedMaxY = visibleBottom - guideHeight - gap
  const minY = paddedMaxY >= paddedMinY ? paddedMinY : visibleTop
  const maxY = paddedMaxY >= paddedMinY ? paddedMaxY : Math.max(visibleTop, visibleBottom - guideHeight)
  const desiredY = handTop - guideHeight - actionButtonHeight - gap * 2
  return Math.min(Math.max(desiredY, minY), maxY)
}

export function assistTileMask(sourceCode: number, targetCode: number): AssistTileMask {
  if (sourceCode <= 0 || targetCode <= 0) return 0
  const sourceKind = (sourceCode >> 4) & 0xF
  const sourceNumber = sourceCode & 0xF
  const targetKind = (targetCode >> 4) & 0xF
  const targetNumber = targetCode & 0xF
  if (sourceKind !== targetKind) return 0
  if (sourceNumber === targetNumber) return 2
  if (sourceKind < 3 && Math.abs(sourceNumber - targetNumber) === 1) return 1
  return 0
}

export function decideTouchTileAction(selectedIdx: number, tappedIdx: number): TouchTileDecision {
  return {
    selectedIdx: tappedIdx,
    confirmDiscard: selectedIdx === tappedIdx,
  }
}

export function decideTimedDiscardIndex(
  handBipaiIndices: readonly (number | undefined)[],
  selectedIdx: number,
  selectedBipaiIndex?: number,
): number {
  if (selectedBipaiIndex !== undefined) {
    const currentIdx = handBipaiIndices.indexOf(selectedBipaiIndex)
    if (currentIdx >= 0) return currentIdx
  }
  if (selectedIdx >= 0 && selectedIdx < handBipaiIndices.length) return selectedIdx
  return handBipaiIndices.length - 1
}

export function decideAutoDiscardDelayMs(
  localDeadlineAt: number | undefined,
  localNow: number,
  timeLimitSeconds: number,
  sendLeadMs = 250,
): number {
  const fallbackMs = Math.max(1000, Math.trunc((Number.isFinite(timeLimitSeconds) && timeLimitSeconds > 0 ? timeLimitSeconds : 5) * 1000))
  const remainingMs = localDeadlineAt !== undefined && Number.isFinite(localDeadlineAt)
    ? Math.max(0, localDeadlineAt - localNow)
    : fallbackMs
  return Math.max(0, remainingMs - Math.max(0, sendLeadMs))
}

export function decideDiscardSource(
  handCount: number,
  handIdx: number,
  afterCall: boolean,
  concealOpponentPosition: boolean,
  seed: number,
): DiscardSourceDecision {
  const lastIdx = Math.max(0, handCount - 1)
  const isTedashi = afterCall || handIdx !== lastIdx
  if (!isTedashi || !concealOpponentPosition) return { isTedashi, displayIdx: Math.max(0, handIdx) }
  const concealedSlots = Math.max(1, handCount - 1)
  const mixedSeed = Math.abs(Math.imul(seed || 1, 1103515245) + 12345)
  return { isTedashi, displayIdx: mixedSeed % concealedSlots }
}